import { createFileRoute, Outlet } from "@tanstack/react-router";
import type { ReactElement } from "react";
import { useTranslation } from "react-i18next";
import { Skeleton } from "@/components/ui/skeleton";
import { useCrmAccess } from "@/hooks/use-crm-access";

export const Route = createFileRoute("/crm")({
	component: CrmLayout,
});

/**
 * The CRM guard: reads the CRM flags of admin.getAccessProfile (cached for
 * the session) and renders the section only for a user with a CRM role. Pages below read
 * the same flags via useCrmPermissions() to hide what the role cannot do.
 */
function CrmLayout(): ReactElement {
	const { t } = useTranslation();
	const { access, isLoading } = useCrmAccess();

	if (isLoading || !access) {
		return (
			<div className="space-y-4">
				<Skeleton className="h-10 w-1/3" />
				<Skeleton className="h-64" />
			</div>
		);
	}

	if (!access.canAccess) {
		return (
			<div
				className="flex flex-col items-center justify-center gap-2 py-16 text-center"
				data-testid="crm-access-denied"
			>
				<h1 className="font-bold text-2xl">
					{t("admin:crm.access.denied_title")}
				</h1>
				<p className="text-muted-foreground">
					{t("admin:crm.access.denied_message")}
				</p>
			</div>
		);
	}

	// CRM pages are dense: compact cards (less space around the card titles).
	return (
		<div className="[&_[data-slot=card-header]]:pb-0 [&_[data-slot=card]]:gap-3 [&_[data-slot=card]]:py-4">
			<Outlet />
		</div>
	);
}
